import { LoadingButton } from '@mui/lab';
import { Avatar, Box, Button, Card, styled, Typography } from '@mui/material';

import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import MobileAppBanner from '../../components/mobileAppBanner/MobileAppBanner';
import { AlertSnackbarContext } from '../../components/snackbars';
import { Spinner } from '../../components/spinners';
import SavePaymentModal from '../../components/stripe/SavePaymentModal';
import ShowMoreText from '../../components/text/ShowMoreText';
import { AlertMessages, ButtonStrings, Color, Defaults, EventDetailStrings, GlobalStrings } from '../../constants';
import { SignUpToEventDto, useSignupEvent } from '../../lib/event/hooks';
import { useGetEventById } from '../../lib/event/hooks/useGetEventById';
import { useGetPaymentInfo } from '../../lib/user/hooks/useGetPaymentInfo';
import { CreatePaymentResponse, useCreatePaymentSheet } from '../../lib/user/usePostPaymentIntent';
import useUser from '../../lib/user/useUser';
import { isIOS, isMobile } from '../../lib/util/checkPlatform';
import { formatEventTime } from '../../lib/util/formatEventTime';
import { hexToRgba } from '../../lib/util/hexToRgba';
import { CompetitionFormat, EventGameStyle, EventInviteStatus } from '../../types/Event';

import ParticipantsList from './ParticipantsList';

const EventDetails = () => {
  const { id } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const success = queryParams.get('success');
  const [, setSnackbar] = useContext(AlertSnackbarContext);
  const { data: event, isLoading: eventIsLoading, error, refetch: refetchEvent } = useGetEventById(id || '');
  const navigate = useNavigate();
  const isJoinedEvent = useMemo(() => event?.rsvpStatus === EventInviteStatus.GOING, [event]);
  const [user] = useUser();
  const [paymentIntent, setPaymentIntent] = useState<CreatePaymentResponse | null>();
  const { mutateAsync: createPaymentIntent } = useCreatePaymentSheet();
  const { data: paymentInfo, refetch: refetchPaymentInfo } = useGetPaymentInfo(user?.id);
  const { mutateAsync: signUpToEvent, isLoading: isSigningUpToEvent } = useSignupEvent();
  const [isSuccess, setSuccess] = useState(success && success === 'true');
  const chargeUser = useCallback(
    () =>
      signUpToEvent({ eventId: id } as SignUpToEventDto, {
        onSuccess: () => {
          refetchEvent();
          window.scrollTo(0, 0);
        },
      }),
    [],
  );
  const { state } = useLocation();
  const isSettingUpPaymentRef = useRef(false);

  const handleSetupPayment = useCallback(() => {
    const requestPaymentIntent = async () => {
      if (isSettingUpPaymentRef.current) return;
      isSettingUpPaymentRef.current = true;
      const response = await createPaymentIntent(id ?? '');
      setPaymentIntent(response);
      // reset state
      navigate(location.pathname, { replace: true, state: null });
    };
    requestPaymentIntent();
  }, [createPaymentIntent, id]);

  useEffect(() => {
    if (!state?.signUpUser || isSettingUpPaymentRef.current || !event) return;
    if (event?.entryFee > 0 && !paymentInfo) {
      handleSetupPayment();
    } else {
      chargeUser();
    }
  }, [state?.signUpUser, isSettingUpPaymentRef.current, paymentInfo]);

  useEffect(() => {
    if (isSuccess && !isJoinedEvent && !eventIsLoading && user && paymentInfo) {
      setSuccess(false);
      refetchPaymentInfo();
      chargeUser();
    }
  }, [isSuccess, eventIsLoading, user, paymentInfo]);

  if (eventIsLoading) {
    return <Spinner />;
  }

  if (error || !event) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography color="error">Event not found.</Typography>
      </Box>
    );
  }

  const formatPrize = (prize: number | undefined) => {
    if (!prize) return '0';
    const rounded = Math.round(prize * 100) / 100;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2);
  };

  const openInGoogleMaps = (latitude: number, longitude: number) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  const handleSignUp = () => {
    if (!user) {
      navigate(`/discover/${id}/sign-up`);
      return;
    }

    if (!paymentInfo && event.entryFee > 0) {
      handleSetupPayment();
      return;
    }

    chargeUser();
  };

  const renderLocationAndDate = () => {
    return (
      <>
        <Section>
          <Typography variant="body2" fontWeight={700}>
            {EventDetailStrings.LOCATION}
          </Typography>
          <StyledRow>
            <img src="/assets/images/location.svg" alt={EventDetailStrings.SKILL_LEVEL} />

            <Typography
              variant="body2"
              style={{
                overflow: 'hidden',
                maxLines: 1,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: event.coordinates ? 'pointer' : 'default',
                color: Color.prussianBlue,
                fontSize: 14,
              }}
              onClick={() => openInGoogleMaps(event.coordinates.latitude, event.coordinates.longitude)}
            >
              {event.fullAddress || event.city || event.state}
            </Typography>

            <Box
              onClick={() => {
                navigator.clipboard.writeText(event.fullAddress || event.city || event.state);
                setSnackbar({
                  message: AlertMessages.LOCATION_COPIED_TO_CLIPBOARD,
                  severity: 'success',
                });
              }}
            >
              <StyledImage src="/assets/images/copy.svg" alt={'Copy Location'} />
            </Box>
          </StyledRow>
        </Section>
        <HorizontalLine />
        <Section>
          <Typography variant="body2" fontWeight={700}>
            {EventDetailStrings.DATE}
          </Typography>
          <EventDate>
            <Typography variant="body2">
              {`${EventDetailStrings.START} ${formatEventTime(event.startUtc, event.timezone ?? '')}`}
            </Typography>
            <Typography variant="body2">{`${EventDetailStrings.END} ${formatEventTime(event.endUtc, event.timezone ?? '')}`}</Typography>
            <Typography variant="body2">
              {`${EventDetailStrings.REGISTER_BY} ${formatEventTime(event.registrationDeadlineUtc, event.timezone ?? '')}`}
            </Typography>
          </EventDate>
        </Section>
        <HorizontalLine />
      </>
    );
  };

  const isOngoingCompetition =
    event.competitionFormat === CompetitionFormat.BEST_SCORE ||
    event.competitionFormat === CompetitionFormat.HOSTS_VOTE ||
    event.competitionFormat === CompetitionFormat.PLAYERS_VOTE;
  const isSignUpEnabled = !event.isMatchesPublished || (!event.isFinished && isOngoingCompetition);

  return (
    <Container>
      {isMobile() && <MobileAppBanner />}

      <LogoContainer marginTop={isMobile() ? '60px' : 0}>
        <img src="/assets/images/logo.svg" alt={GlobalStrings.GLOBAL_PROJECT_NAME} />
      </LogoContainer>

      <StyledCard>
        {isJoinedEvent && (
          <SignUpSuccessBanner>
            <img src="/assets/images/success.svg" alt={EventDetailStrings.ACCESSIBILITY_SIGN_UP_SUCCESS} />
            <Typography style={{ fontWeight: 400 }}>{EventDetailStrings.SIGN_UP_SUCCESS}</Typography>
          </SignUpSuccessBanner>
        )}
        <Typography variant="h5">{event.name}</Typography>
        <EventImageContainer>
          {event.coverMedia?.largeUrl ? <EventImage src={event.coverMedia?.mediumUrl} alt={event.name} /> : null}

          {!event.charity && event.entryFee > 0 && (
            <CashPrizeBadgeContainer>
              <CashPrizeBadge>{`${EventDetailStrings.WIN} $${formatPrize(event.maxPrize)}`}</CashPrizeBadge>
            </CashPrizeBadgeContainer>
          )}
        </EventImageContainer>

        {event.announcement && (
          <>
            <Section>
              <Typography variant="body2" fontWeight={700}>
                {EventDetailStrings.ANNOUNCEMENT}
              </Typography>
              <AnnouncementBox>{event.announcement}</AnnouncementBox>
            </Section>

            <HorizontalLine />
          </>
        )}

        <Section>
          <Typography variant="body2" fontWeight={700}>
            {EventDetailStrings.DESCRIPTION}
          </Typography>
          <ShowMoreText text={event.description || EventDetailStrings.NO_DESCRIPTION} />
        </Section>
        <HorizontalLine />

        {!isJoinedEvent && (
          <>
            <Section>
              <Typography variant="body2" fontWeight={700}>
                {EventDetailStrings.ENTRY_FEE}
              </Typography>
              <Typography variant="body2">{event.entryFee ? `$${event.entryFee}` : EventDetailStrings.FREE}</Typography>
            </Section>
            <HorizontalLine />
          </>
        )}
        {isJoinedEvent && renderLocationAndDate()}

        <Section>
          <Typography variant="body2" fontWeight={700}>
            {EventDetailStrings.SKILL_LEVEL}
          </Typography>
          <StyledRow>
            <img src="/assets/images/star.svg" alt={EventDetailStrings.SKILL_LEVEL} />
            <Typography variant="body2">{`${event.minSkillLevel}-${event.maxSkillLevel}`}</Typography>
          </StyledRow>
        </Section>
        <HorizontalLine />

        {isJoinedEvent && (
          <>
            <HostBox>
              <Section>
                <Typography variant="body2" fontWeight={700}>
                  {EventDetailStrings.HOST}
                </Typography>
                <Box display={'flex'} flexDirection={'row'} gap={'10px'}>
                  <Avatar src={event.hostUser?.profileMedia?.smallUrl || ''} />
                  <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant="body2">{event.hostUser?.fullname || 'Unknown Host'}</Typography>
                    <Typography variant="body2" color={Color.oldSilver} fontSize={14}>
                      {`${event.hostRating}/5 ${EventDetailStrings.HOST_RATING}`}
                    </Typography>
                  </Box>
                </Box>
              </Section>
            </HostBox>
            <HorizontalLine />
          </>
        )}

        {!isJoinedEvent && renderLocationAndDate()}

        {isSignUpEnabled && !isJoinedEvent && (
          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleSignUp}
            disabled={isSigningUpToEvent}
            loading={isSigningUpToEvent}
          >
            {ButtonStrings.BUTTON_SIGN_UP}
          </StyledButton>
        )}

        {isJoinedEvent && (
          <MoreInfoBanner>
            <Typography fontWeight={700} color={Color.white}>
              {EventDetailStrings.SIGN_UP_MORE_INFO}
            </Typography>
            <StyledRow justifyContent={'space-between'}>
              <Button
                startIcon={<img src={'/assets/images/arrow-down.svg'} alt={GlobalStrings.GLOBAL_PROJECT_NAME} />}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  backgroundColor: Color.maximumYellowRed,
                  '&:hover': {
                    backgroundColor: hexToRgba(Color.maximumYellowRed, 0.5),
                  },
                  fontWeight: 700,
                  fontSize: 11,
                  color: Color.prussianBlue,
                  radius: 8,
                  height: 34,
                }}
                onClick={() => window.open(isIOS() ? Defaults.APPSTORE_LINK : Defaults.PLAYSTORE_LINK, '_blank')}
              >
                {ButtonStrings.BUTTON_INSTALL_APP}
              </Button>
              {event.participants?.length > 0 && (
                <ParticipantsList
                  participants={event.participants}
                  groups={event.groups}
                  isTeam={event.gameStyle === EventGameStyle.TEAM}
                  total={event.totalParticipants}
                />
              )}
            </StyledRow>
          </MoreInfoBanner>
        )}

        <DownloadApp>
          <StyledImage
            src="/assets/images/apple.png"
            alt={EventDetailStrings.ACCESSIBILITY_IOS_DOWNLOAD}
            onClick={() => window.open(Defaults.APPSTORE_LINK, '_blank')}
            style={{ width: '100%', objectFit: 'cover' }}
          />
          <StyledImage
            src="/assets/images/android.png"
            alt={EventDetailStrings.ACCESSIBILITY_ANDROID_DOWNLOAD}
            onClick={() => window.open(Defaults.PLAYSTORE_LINK, '_blank')}
            style={{ width: '100%', objectFit: 'cover' }}
          />
        </DownloadApp>

        {paymentIntent && (
          <SavePaymentModal
            paymentIntent={paymentIntent}
            onClose={() => {
              isSettingUpPaymentRef.current = false;
              setPaymentIntent(null);
            }}
          />
        )}
      </StyledCard>
    </Container>
  );
};

export default memo(EventDetails);

const Container = styled(Box)({
  alignContent: 'center',
  justifyContent: 'center',
  background: Color.cultured,
  borderRadius: '16px',
  padding: '24px',
  paddingBottom: '74px',
});

const EventImage = styled('img')({
  width: '100%',
  height: 223,
  objectFit: 'cover',
  border: '1px solid rgba(130, 133, 135, 0.1)',
  borderRadius: 10,
});

const Section = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  flex: 1,
});

const AnnouncementBox = styled(Box)({
  background: 'rgba(99, 190, 99, 0.1)',
  border: '1px solid rgba(99, 190, 99, 0.5)',
  padding: '16px 20px',
  borderRadius: '10px',
  minHeight: 104,
});

const StyledCard = styled(Card)({
  maxWidth: 400,
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  borderRadius: 16,
  alignSelf: 'center',
  margin: '0 auto',
  gap: 16,
});

const HorizontalLine = styled(Box)({
  width: '100%',
  height: 1,
  background: 'rgba(130, 133, 135, 0.1)',
});

const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  borderRadius: 8,
  backgroundColor: Color.cultured,
  marginBottom: 18,
});

const EventImageContainer = styled(Box)({
  position: 'relative',
});

const CashPrizeBadgeContainer = styled(Box)({
  width: '100%',
  position: 'absolute',
  bottom: 16,
  right: 10,
  backgroundColor: Color.badgeBackground,
});

const CashPrizeBadge = styled(Box)({
  position: 'absolute',
  bottom: 10,
  right: 10,
  backgroundColor: Color.badgeBackground,
  padding: '2px 6px',
  borderRadius: 7,
  color: Color.japaneseLaurel,
  fontWeight: 700,
});

const StyledRow = styled(Box)({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  verticalAlign: 'center',
  alignContent: 'center',
});

const EventDate = styled(Box)({
  display: 'flex',
  gap: 4,
  flexDirection: 'column',
});

const StyledImage = styled('img')({
  cursor: 'pointer',
});

const StyledButton = styled(LoadingButton)({
  left: '50%',
  transform: 'translateX(-50%)',
  fontWeight: 400,
  fontSize: 16,
  position: 'fixed',
  bottom: 10,
  width: '80%',
  maxWidth: '380px',
});

const SignUpSuccessBanner = styled(Box)({
  backgroundColor: hexToRgba(Color.mantis, 0.1),
  borderRadius: '10px',
  border: `1px solid ${hexToRgba(Color.mantis, 0.5)}`,
  padding: '10px',
  display: 'flex',
  flex: 1,
  alignContent: 'center',
  alignItems: 'center',
  gap: '5px',
});

const HostBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  filter: 'blur(2px)',
});

const MoreInfoBanner = styled(Box)({
  backgroundColor: Color.prussianBlue,
  borderRadius: '10px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: '12px',
});

const DownloadApp = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
});
